import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Brand } from "@/components/agent/AgentLayout";
import { FlightPath, resetFlight } from "@/components/applicant/FlightPath";
import { PassLabel } from "@/components/applicant/JourneyPanel";
import { Field, PrimaryButton, errorCls, inputCls } from "@/components/shared/ui";
import { prefersReducedMotion } from "@/components/landing/stage";
import { parseApplicationLink } from "@/lib/applicationLink";
import { APP_URL, agentDestination, external, linkProps } from "@/components/landing/destinations";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TAKEOFF_MS = 750;

/** Top-right "I'm a letting agent": an in-app route, or a link out when this page runs outside the app. */
const AgentLink = () => {
  const props = linkProps(agentDestination());
  const cls = "rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground";
  return props.to
    ? <Link {...props} className={cls} data-testid="tenant-start-agent-link">I'm a letting agent</Link>
    : <a {...props} className={cls} data-testid="tenant-start-agent-link">I'm a letting agent</a>;
};

/** "I'm a tenant": paste the link your letting agent sent, and we open your application. */
export default function TenantStart() {
  const navigate = useNavigate();
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [flying, setFlying] = useState(false);
  const timer = useRef(null);
  const path = parseApplicationLink(link);

  useEffect(() => {
    resetFlight();
    return () => clearTimeout(timer.current);
  }, []);

  const open = (e) => {
    e.preventDefault();
    if (external && path) {
      // Outside the app the link itself opens the application in a new tab; just let the plane take off here.
      setFlying(true);
      return;
    }
    if (!path) {
      setError(link.trim() ? "That doesn't look like a Landin application link. Check you copied all of it, starting with https://" : "Paste the link from your letting agent first.");
      return;
    }
    setError("");
    setFlying(true);
    // Let the plane take off, then open the application with a fresh flight path.
    timer.current = setTimeout(() => {
      resetFlight();
      navigate(path);
    }, prefersReducedMotion() ? 0 : TAKEOFF_MS);
  };

  return (
    <div className="relative isolate min-h-screen overflow-x-clip bg-background text-sm text-foreground" data-testid="tenant-start-page">
      <header className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-8">
        <Link to="/welcome" aria-label="Landin home"><Brand /></Link>
        <AgentLink />
      </header>
      <main className="mx-auto max-w-md px-4 pb-16 pt-6 md:pt-16">
        <Link to="/welcome" className="-ml-1 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-xs text-muted-foreground hover:text-foreground" data-testid="tenant-start-back">
          <ArrowLeft className="size-3.5" /> Back
        </Link>
        <form onSubmit={open} noValidate className="mt-4 rounded-3xl border bg-background p-6 shadow-[0_24px_60px_-30px_rgb(0_0_0/0.3)] md:p-8" data-testid="tenant-link-form">
          <FlightPath t={flying ? 1 : 0} stops={[0.25, 0.5, 0.75]} testid="tenant-start-flight" />
          <PassLabel className="mt-6">For tenants</PassLabel>
          <h1 className="mt-2 text-[26px] font-semibold leading-tight tracking-tight">Open your application</h1>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">Paste the link your letting agent sent you. It usually comes by email or text message.</p>
          <div className="mt-6">
            <Field label="Your application link">
              <input type="text" inputMode="url" autoComplete="off" autoCapitalize="none" spellCheck={false} autoFocus value={link}
                onChange={(e) => { setLink(e.target.value); setError(""); }} placeholder="https://landin.app/apply/…"
                aria-invalid={error ? "true" : undefined} aria-describedby="tenant-link-status"
                className={cn(inputCls, "h-11 text-base")} data-testid="tenant-link-input" />
            </Field>
            <p id="tenant-link-status" className="mt-2 min-h-5 text-sm" aria-live="polite">
              {error ? <span className={errorCls} data-testid="tenant-link-error">{error}</span>
                : path ? <span className="inline-flex items-center gap-1.5 text-success" data-testid="tenant-link-ok"><Check className="size-4" strokeWidth={2.5} /> Application link found</span>
                : null}
            </p>
          </div>
          {external && path ? (
            <a href={`${APP_URL}${path}`} target="_blank" rel="noopener noreferrer" onClick={() => setFlying(true)}
              className={cn(buttonVariants({ size: "xl" }), "mt-4 w-full")} data-testid="tenant-link-submit">
              Open my application <ArrowRight />
            </a>
          ) : (
            <PrimaryButton type="submit" size="xl" className="mt-4 w-full" isLoading={flying} testid="tenant-link-submit">
              Open my application {!flying && <ArrowRight />}
            </PrimaryButton>
          )}
          <p className="mt-5 text-center text-sm text-muted-foreground">No link yet? Ask your letting agent to send you their Landin apply link.</p>
        </form>
      </main>
    </div>
  );
}
